// Smoke-test page for the per-part wizard (M2 Phase 5.3).
//
// Hosts a standalone PartWizard with a hardcoded initial part. Lets you
// click through every step for every part type without needing the full
// quote-builder flow wired up (that lands in M2 Phase 5.3b).
//
// Drop a `console.log` is the deliverable here — saving simply prints
// the assembled draft so we can verify field plumbing end-to-end.

import { useState } from 'react';
import type { Part, PartType } from '@/shared/crmTypes';
import { PartWizard } from '@/components/PartWizard/PartWizard';
import { PART_TYPE_META } from '@/components/PartTypeBadge';

const TYPES: PartType[] = ['machined', 'cut_blank', 'weldment', 'hybrid', 'purchased'];

function blankPart(type: PartType): Partial<Part> & { type: PartType } {
  return {
    type,
    number: '',
    description: '',
    qtyTiers: [10, 50, 100],
  };
}

export function PartWizardTestPage() {
  const [type, setType] = useState<PartType>('machined');
  const [part, setPart] = useState<Partial<Part> & { type: PartType }>(blankPart('machined'));
  const [lastSaved, setLastSaved] = useState<unknown>(null);

  function switchType(next: PartType) {
    setType(next);
    setPart(blankPart(next));
    setLastSaved(null);
  }

  function handleChange(updates: Partial<Part>) {
    setPart((prev) => ({ ...prev, ...updates }));
  }

  function handleSave(p: Partial<Part> & { type: PartType }) {
    // eslint-disable-next-line no-console
    console.log('[PartWizardTestPage] onSave', p);
    setLastSaved(p);
  }

  function handleSaveDraft(p: Partial<Part> & { type: PartType }) {
    // eslint-disable-next-line no-console
    console.log('[PartWizardTestPage] onSaveDraft', p);
  }

  function handleCancel() {
    setPart(blankPart(type));
    setLastSaved(null);
  }

  return (
    <div className="max-w-3xl mx-auto">
      <div className="mb-4">
        <h1 className="text-2xl font-bold text-navy">Part Wizard — smoke test</h1>
        <p className="text-sm text-text-mute mt-1">
          M2 Phase 5.3 deliverable. Click through every step for every part type to verify
          field plumbing. Save logs the assembled draft to console; nothing persists.
        </p>
      </div>

      {/* Part-type switcher — lets a single test page exercise all five flows. */}
      <div className="bg-paper border border-line rounded p-3 mb-4">
        <div className="text-xs font-semibold text-text-mute uppercase tracking-wide mb-2">
          Test part type
        </div>
        <div className="flex flex-wrap gap-2">
          {TYPES.map((t) => {
            const meta = PART_TYPE_META[t];
            const active = type === t;
            return (
              <button
                key={t}
                type="button"
                onClick={() => switchType(t)}
                className={`px-3 py-1.5 text-sm rounded border ${
                  active
                    ? 'bg-orange text-white border-orange font-semibold'
                    : 'bg-paper text-text border-line-dark hover:border-orange'
                }`}
              >
                <span aria-hidden>{meta.icon}</span> {meta.label}
              </button>
            );
          })}
        </div>
        <div className="text-[11px] text-text-mute italic mt-2">
          Switching wipes the current draft.
        </div>
      </div>

      {/* The wizard itself. Wrapped in a keyed div so internal state
          (currentIndex/doneIndices) resets on type change. */}
      <div key={type} className="mb-4">
        <PartWizard
          part={part}
          onChange={handleChange}
          onSave={handleSave}
          onCancel={handleCancel}
          onSaveDraft={handleSaveDraft}
        />
      </div>

      {/* Live state inspector — handy when poking at the wizard. */}
      <details className="bg-paper border border-line rounded p-3 mb-4">
        <summary className="cursor-pointer text-sm font-semibold text-navy">
          Live draft (debug)
        </summary>
        <pre className="text-[11px] font-mono whitespace-pre-wrap mt-2 bg-bg/50 p-2 rounded border border-line max-h-80 overflow-auto">
          {JSON.stringify(part, null, 2)}
        </pre>
      </details>

      {lastSaved !== null && (
        <div className="bg-green/10 border-l-4 border-green rounded-r px-3 py-2 text-sm">
          <span aria-hidden>✅</span> Last save logged to console.{' '}
          <button
            type="button"
            onClick={() => setLastSaved(null)}
            className="text-orange hover:underline ml-2 text-xs"
          >
            dismiss
          </button>
        </div>
      )}
    </div>
  );
}
